// Rich medical content for conditions treated by Doctor Kichian's Gastroenterology.
// Structure: slug, name, icon (lucide), summary, symptoms, causes, diagnosis, treatments, lifestyle, when_to_seek_care.

export const CONDITIONS = [
    {
        slug: "gerd",
        name: "Acid Reflux (GERD)",
        icon: "Flame",
        category: "Upper GI",
        summary:
            "Gastroesophageal reflux disease occurs when stomach contents repeatedly wash back into the esophagus, causing heartburn, regurgitation, and — over time — potential damage to the esophageal lining.",
        symptoms: [
            "Burning sensation in the chest (heartburn), often after meals or at night",
            "Regurgitation of food or a sour taste in the mouth",
            "Difficulty or pain with swallowing",
            "Chronic cough, hoarseness, or throat clearing",
            "Feeling of a lump in the throat",
        ],
        causes: [
            "Weakening of the lower esophageal sphincter (LES)",
            "Hiatal hernia",
            "Delayed stomach emptying",
            "Contributing factors: obesity, pregnancy, tobacco, certain medications, late-night eating",
        ],
        diagnosis: [
            "Clinical evaluation and symptom review",
            "Trial of acid-suppression therapy",
            "Upper endoscopy (gastroscopy) to assess for esophagitis, Barrett's esophagus, or complications",
            "Ambulatory pH monitoring or high-resolution manometry when indicated",
        ],
        treatments: [
            "Lifestyle modifications: weight optimization, meal timing, sleep positioning",
            "Acid-suppressing medications (H2 blockers, proton pump inhibitors)",
            "Alginate therapies for post-meal reflux",
            "Endoscopic or surgical antireflux options in selected cases",
        ],
        lifestyle: [
            "Avoid eating within 3 hours of bedtime",
            "Elevate the head of the bed 6–8 inches",
            "Reduce known triggers: alcohol, tobacco, large fatty meals, and — individually — chocolate, mint, citrus",
            "Modest weight loss if applicable",
        ],
        when_to_seek_care: [
            "Symptoms more than twice a week or unresponsive to OTC therapy",
            "Difficulty swallowing, unintentional weight loss, or blood in vomit/stool",
            "Symptoms lasting more than 5 years — evaluation for Barrett's esophagus may be indicated",
        ],
    },
    {
        slug: "crohns-disease",
        name: "Crohn's Disease",
        icon: "Activity",
        category: "Inflammatory Bowel Disease",
        summary:
            "Crohn's disease is a chronic inflammatory condition that can affect any part of the digestive tract — most often the end of the small intestine and the beginning of the colon.",
        symptoms: [
            "Persistent diarrhea, often with abdominal cramping",
            "Unintentional weight loss and fatigue",
            "Abdominal pain, often in the lower right quadrant",
            "Rectal bleeding, fistulas, or perianal disease",
            "Extra-intestinal symptoms: joint pain, skin rashes, eye inflammation",
        ],
        causes: [
            "Immune dysregulation in genetically predisposed individuals",
            "Environmental triggers (smoking, dietary and microbial factors)",
            "Family history of IBD increases risk",
        ],
        diagnosis: [
            "Colonoscopy with biopsies",
            "Cross-sectional imaging: MR enterography or CT enterography",
            "Blood work and stool markers (fecal calprotectin)",
            "Capsule endoscopy when small-bowel disease is suspected",
        ],
        treatments: [
            "Biologic therapies (anti-TNFs, anti-integrins, anti-IL-23s)",
            "Small-molecule therapies (JAK inhibitors, S1P modulators)",
            "Immunomodulators and corticosteroids for flares",
            "Nutritional therapy, and surgery when indicated",
        ],
        lifestyle: [
            "Structured monitoring even in remission — biomarkers, imaging, and endoscopy",
            "Smoking cessation (smoking worsens Crohn's)",
            "Vaccination review before starting immunosuppressants",
            "Nutritional support and mental health resources",
        ],
        when_to_seek_care: [
            "New or worsening diarrhea, rectal bleeding, weight loss, or abdominal pain",
            "Fever with abdominal symptoms",
            "Perianal drainage or new fistulas",
        ],
    },
    {
        slug: "ulcerative-colitis",
        name: "Ulcerative Colitis",
        icon: "Activity",
        category: "Inflammatory Bowel Disease",
        summary:
            "Ulcerative colitis is a chronic inflammatory bowel disease that affects the innermost lining of the colon and rectum, causing inflammation and ulcers that produce diarrhea, urgency, and bleeding.",
        symptoms: [
            "Bloody diarrhea with mucus",
            "Urgency and frequent bowel movements",
            "Lower abdominal cramping and tenesmus",
            "Fatigue, weight loss, and, in severe cases, fever",
        ],
        causes: [
            "Immune-mediated inflammation in genetically susceptible individuals",
            "Environmental factors; interestingly, smoking is protective (never a recommendation)",
            "Family history increases risk",
        ],
        diagnosis: [
            "Colonoscopy with biopsies confirming continuous inflammation from the rectum",
            "Fecal calprotectin, CRP, and CBC for activity monitoring",
            "Ruling out infectious causes with stool testing",
        ],
        treatments: [
            "5-aminosalicylates (mesalamine) for mild to moderate disease",
            "Biologics and small-molecule therapies for moderate to severe disease",
            "Short courses of corticosteroids for flares",
            "Colectomy in selected refractory or high-risk cases",
        ],
        lifestyle: [
            "Adherence to maintenance therapy — even in remission",
            "Colonoscopic surveillance for dysplasia after 8+ years of disease",
            "Bone-health monitoring, especially with steroid exposure",
            "Mental health support: chronic disease affects mood and vice versa",
        ],
        when_to_seek_care: [
            "Increase in stool frequency, urgency, or blood",
            "Fever with GI symptoms",
            "Any signs of severe flare: dehydration, dizziness, tachycardia",
        ],
    },
    {
        slug: "ibs",
        name: "Irritable Bowel Syndrome (IBS)",
        icon: "Sparkles",
        category: "Functional GI",
        summary:
            "IBS is a common disorder of gut–brain interaction causing recurrent abdominal pain associated with changes in bowel habits (diarrhea, constipation, or both), without structural disease.",
        symptoms: [
            "Recurrent abdominal pain linked to bowel movements",
            "Diarrhea, constipation, or alternating patterns",
            "Bloating and abdominal distension",
            "Mucus in the stool",
            "Symptoms often triggered by meals or stress",
        ],
        causes: [
            "Altered gut–brain signaling and visceral hypersensitivity",
            "Microbiome differences",
            "Post-infectious IBS after acute gastroenteritis",
            "Stress and psychosocial contributors that amplify symptoms",
        ],
        diagnosis: [
            "Symptom-based Rome IV criteria",
            "Selective testing to rule out celiac disease, IBD, thyroid dysfunction, or lactose intolerance",
            "Colonoscopy if red-flag features exist (bleeding, weight loss, age > 50, family history)",
        ],
        treatments: [
            "Dietary strategies including low-FODMAP under supervision",
            "Antispasmodics, secretagogues, or motility agents based on subtype",
            "Gut-directed cognitive behavioral therapy and hypnotherapy",
            "Peppermint oil, soluble fiber, and probiotic trials in select cases",
        ],
        lifestyle: [
            "Regular meal timing and adequate hydration",
            "Sleep, exercise, and stress-management practices",
            "Symptom journaling to identify individual triggers",
        ],
        when_to_seek_care: [
            "Symptoms that disrupt daily life",
            "Any red-flag features: weight loss, bleeding, anemia, nocturnal symptoms",
            "Family history of colon cancer, IBD, or celiac disease",
        ],
    },
    {
        slug: "celiac-disease",
        name: "Celiac Disease",
        icon: "Wheat",
        category: "Small Bowel",
        summary:
            "Celiac disease is an autoimmune condition triggered by gluten in genetically susceptible individuals, causing damage to the small intestinal lining and a wide range of digestive and non-digestive symptoms.",
        symptoms: [
            "Chronic diarrhea, bloating, or abdominal discomfort",
            "Unintentional weight loss and malnutrition",
            "Iron-deficiency anemia not otherwise explained",
            "Fatigue, brain fog, and osteoporosis",
            "Skin: dermatitis herpetiformis",
        ],
        causes: [
            "Autoimmune response to gluten in wheat, barley, and rye",
            "Strong genetic component (HLA-DQ2/DQ8)",
            "Family history significantly increases risk",
        ],
        diagnosis: [
            "Serologic testing: tissue transglutaminase (tTG-IgA) with total IgA",
            "Small-bowel biopsy via gastroscopy for confirmation",
            "Do NOT start a gluten-free diet before testing — it obscures results",
        ],
        treatments: [
            "Strict, lifelong gluten-free diet with dietitian guidance",
            "Replete deficiencies: iron, vitamin D, B12, folate",
            "Bone-density evaluation at diagnosis",
            "Regular follow-up with serology to confirm healing",
        ],
        lifestyle: [
            "Read labels carefully; watch for cross-contamination",
            "Screen first-degree relatives",
            "Address associated conditions (thyroid, diabetes) when present",
        ],
        when_to_seek_care: [
            "Suspected symptoms, especially with family history",
            "Unexplained anemia, osteoporosis, or infertility",
        ],
    },
    {
        slug: "chronic-constipation",
        name: "Chronic Constipation",
        icon: "Timer",
        category: "Lower GI",
        summary:
            "Chronic constipation is more than just infrequent stools — it includes straining, hard stools, incomplete evacuation, and impact on daily life. It has multiple causes and effective, individualized treatments.",
        symptoms: [
            "Fewer than 3 bowel movements per week",
            "Straining, hard or lumpy stools",
            "Sensation of incomplete evacuation or blockage",
            "Abdominal discomfort and bloating",
        ],
        causes: [
            "Slow-transit constipation",
            "Pelvic floor dysfunction / dyssynergic defecation",
            "Medication-induced (opioids, anticholinergics, iron, calcium)",
            "Endocrine/metabolic (hypothyroidism, diabetes) or neurologic causes",
        ],
        diagnosis: [
            "History and rectal examination",
            "Lab and thyroid evaluation when indicated",
            "Anorectal manometry and transit studies in refractory cases",
            "Colonoscopy if red-flag features are present",
        ],
        treatments: [
            "Fiber and hydration optimization",
            "Osmotic and stimulant laxatives titrated to response",
            "Secretagogues (linaclotide, plecanatide) for select patients",
            "Pelvic floor physical therapy for dyssynergia",
        ],
        lifestyle: [
            "Regular toileting routine (usually 20–30 minutes after meals)",
            "Adequate soluble fiber (psyllium)",
            "Movement and hydration",
        ],
        when_to_seek_care: [
            "New-onset constipation after age 50",
            "Blood in stool, weight loss, or family history of colon cancer",
            "Constipation unresponsive to first-line strategies",
        ],
    },
    {
        slug: "chronic-diarrhea",
        name: "Chronic Diarrhea",
        icon: "Droplets",
        category: "Lower GI",
        summary:
            "Diarrhea lasting more than four weeks warrants evaluation. Causes range from dietary and functional to autoimmune and infectious — targeted testing usually finds the answer.",
        symptoms: [
            "Loose or watery stools lasting more than 4 weeks",
            "Urgency, cramping, or incontinence",
            "Weight loss or nutritional deficiencies",
            "Nocturnal diarrhea (a red flag)",
        ],
        causes: [
            "IBS-D, microscopic colitis, IBD, or celiac disease",
            "Bile-acid diarrhea and small-intestinal bacterial overgrowth (SIBO)",
            "Medications (metformin, PPIs, magnesium, antibiotics)",
            "Lactose or fructose intolerance; artificial sweeteners",
        ],
        diagnosis: [
            "Focused history including medications, travel, diet",
            "Stool studies: infection, calprotectin, fecal fat",
            "Bloodwork: celiac serology, thyroid, CBC, CMP",
            "Colonoscopy with biopsies for microscopic colitis when indicated",
        ],
        treatments: [
            "Treat underlying cause (celiac diet, IBD therapy, etc.)",
            "Bile-acid binders (cholestyramine) for bile-acid diarrhea",
            "Targeted antibiotics for SIBO",
            "Symptom-based agents (loperamide) when appropriate",
        ],
        lifestyle: [
            "Trial-based elimination diets under guidance",
            "Hydration and electrolyte awareness",
            "Symptom diary to identify patterns",
        ],
        when_to_seek_care: [
            "Diarrhea > 4 weeks",
            "Blood in stool, weight loss, fever, nocturnal symptoms",
            "Dehydration or severe symptoms",
        ],
    },
    {
        slug: "abdominal-pain",
        name: "Abdominal Pain",
        icon: "AlertCircle",
        category: "General GI",
        summary:
            "Persistent or recurring abdominal pain can have many causes — from functional gut disorders to structural, gynecologic, or metabolic conditions. A careful, structured evaluation is essential.",
        symptoms: [
            "Location and pattern of pain (upper, lower, right, left)",
            "Timing: postprandial, nocturnal, positional, cyclical",
            "Associated symptoms: bloating, bowel changes, weight loss, bleeding",
            "Alarm features that require urgent evaluation",
        ],
        causes: [
            "Functional dyspepsia and IBS",
            "Peptic ulcer disease, gallstones, pancreatitis",
            "IBD, endometriosis, or musculoskeletal contributors",
            "Medications and food intolerances",
        ],
        diagnosis: [
            "Comprehensive history and focused exam",
            "Labs (CBC, CMP, lipase, celiac, thyroid) and imaging as guided by history",
            "Endoscopy or colonoscopy when indicated",
        ],
        treatments: [
            "Target the underlying cause; symptom relief in parallel",
            "Neuromodulators for functional pain when appropriate",
            "Dietary strategies and lifestyle adjustments",
        ],
        lifestyle: [
            "Pain and food diaries to detect patterns",
            "Stress management and sleep hygiene",
        ],
        when_to_seek_care: [
            "Severe or persistent pain",
            "Pain with fever, vomiting blood, black stools, or unintentional weight loss",
            "New abdominal pain in adults over 50",
        ],
    },
    {
        slug: "gi-bleeding",
        name: "Gastrointestinal Bleeding",
        icon: "Droplet",
        category: "General GI",
        summary:
            "GI bleeding can be visible (bright red or dark stools, vomiting blood) or hidden (iron-deficiency anemia). Every episode deserves prompt, structured evaluation.",
        symptoms: [
            "Bright red blood in stool or on toilet paper",
            "Black, tarry stools (melena)",
            "Vomiting blood or coffee-ground material",
            "Fatigue and iron-deficiency anemia without obvious cause",
        ],
        causes: [
            "Upper GI: ulcers, esophagitis, Mallory–Weiss tears, varices",
            "Lower GI: diverticulosis, angiodysplasia, colitis, colon polyps or cancer",
            "Medication-related: NSAIDs, aspirin, anticoagulants",
        ],
        diagnosis: [
            "Rapid assessment and stabilization when acute",
            "Upper endoscopy and/or colonoscopy",
            "Capsule endoscopy or CT angiography for obscure bleeding",
        ],
        treatments: [
            "Endoscopic therapy: clips, thermal, injection, banding",
            "Acid suppression, iron replacement, medication review",
            "Interventional radiology or surgery in selected cases",
        ],
        lifestyle: [
            "Medication review — NSAIDs and anticoagulants",
            "Iron supplementation as prescribed",
            "Follow-up to confirm healing",
        ],
        when_to_seek_care: [
            "Any large-volume bleeding or vomiting blood — seek emergency care",
            "New or persistent black stools or blood in stool",
            "Unexplained iron-deficiency anemia",
        ],
    },
    {
        slug: "colon-polyps",
        name: "Colon Polyps",
        icon: "CircleDot",
        category: "Lower GI",
        summary:
            "Colon polyps are common growths in the lining of the colon. Most are harmless, but some can progress to cancer over years — which is why detection and removal during colonoscopy is so effective.",
        symptoms: [
            "Usually asymptomatic",
            "Occasionally: rectal bleeding, mucus, or change in bowel habits",
        ],
        causes: [
            "Age (risk increases after 45)",
            "Family history and genetic syndromes (Lynch, FAP)",
            "Lifestyle contributors: obesity, tobacco, high red-meat intake",
        ],
        diagnosis: [
            "Colonoscopy — detection AND removal in the same procedure",
            "Histologic classification (tubular, tubulovillous, villous, sessile serrated)",
            "Genetic evaluation when multiple polyps or strong family history",
        ],
        treatments: [
            "Polypectomy at colonoscopy for most polyps",
            "Advanced endoscopic techniques (EMR/ESD) for larger lesions",
            "Personalized surveillance intervals based on polyp features",
        ],
        lifestyle: [
            "Screening on schedule",
            "Fiber-rich, whole-food dietary pattern",
            "Physical activity and weight management",
        ],
        when_to_seek_care: [
            "Age 45+ for baseline screening",
            "Family history of colon cancer or polyps",
            "Rectal bleeding or changes in bowel habits",
        ],
    },
    {
        slug: "fatty-liver",
        name: "Fatty Liver Disease",
        icon: "Droplets",
        category: "Liver",
        summary:
            "Metabolic dysfunction–associated steatotic liver disease (MASLD, formerly NAFLD) is the most common liver disease worldwide. Detected early, it is often reversible with targeted lifestyle changes.",
        symptoms: [
            "Often asymptomatic — detected on blood tests or imaging",
            "Occasionally: fatigue, mild right-upper abdominal discomfort",
            "Advanced disease: signs of cirrhosis",
        ],
        causes: [
            "Metabolic syndrome, obesity, type 2 diabetes",
            "High-calorie, high-sugar dietary patterns",
            "Certain medications",
            "Genetic predisposition",
        ],
        diagnosis: [
            "Liver enzymes and metabolic profile",
            "Non-invasive fibrosis scores (FIB-4)",
            "Elastography (transient or MR) to assess fibrosis stage",
            "Liver biopsy in selected cases",
        ],
        treatments: [
            "Structured weight reduction (7–10% of body weight)",
            "Mediterranean-style dietary pattern",
            "Physical activity and management of metabolic risk factors",
            "Emerging pharmacologic therapies for select patients",
        ],
        lifestyle: [
            "Minimize sugar-sweetened beverages and ultra-processed foods",
            "Alcohol moderation or avoidance",
            "Coordinate with primary care for diabetes and lipid management",
        ],
        when_to_seek_care: [
            "Elevated liver enzymes on routine labs",
            "Metabolic risk factors (obesity, diabetes, high triglycerides)",
            "Fatigue or right-upper abdominal discomfort",
        ],
    },
    {
        slug: "barretts-esophagus",
        name: "Barrett's Esophagus",
        icon: "AlertTriangle",
        category: "Upper GI",
        summary:
            "Barrett's esophagus is a change in the lining of the esophagus caused by long-standing acid reflux. It slightly increases the risk of esophageal cancer, which is why surveillance is important.",
        symptoms: [
            "Often no direct symptoms beyond those of GERD",
            "Chronic heartburn or regurgitation is the most common context",
        ],
        causes: [
            "Long-standing gastroesophageal reflux disease",
            "Male sex, older age, central obesity, tobacco use, family history",
        ],
        diagnosis: [
            "Upper endoscopy with targeted biopsies",
            "Histologic confirmation and dysplasia grading",
        ],
        treatments: [
            "Aggressive control of acid reflux",
            "Endoscopic surveillance at intervals based on dysplasia grade",
            "Endoscopic eradication therapies (RFA, EMR) for dysplasia",
        ],
        lifestyle: [
            "Weight management and reflux precautions",
            "Smoking cessation and alcohol moderation",
            "Adherence to surveillance schedule",
        ],
        when_to_seek_care: [
            "Long-standing reflux — discuss screening endoscopy",
            "New or worsening swallowing symptoms",
            "Blood in vomit or black stools — urgent evaluation",
        ],
    },
    {
        slug: "diverticular-disease",
        name: "Diverticular Disease",
        icon: "CircleEllipsis",
        category: "Lower GI",
        summary:
            "Diverticula are small pouches that form in the colon wall. Most people never have symptoms, but occasionally they can bleed (diverticular bleeding) or become inflamed (diverticulitis).",
        symptoms: [
            "Often none (diverticulosis)",
            "Diverticulitis: left lower abdominal pain, fever, altered bowel habits",
            "Diverticular bleeding: painless, often significant red or maroon rectal bleeding",
        ],
        causes: [
            "Aging and structural changes in the colon wall",
            "Contributing factors: low-fiber diet, sedentary lifestyle, tobacco",
        ],
        diagnosis: [
            "CT scan for suspected diverticulitis",
            "Colonoscopy after resolution for surveillance and confirmation",
        ],
        treatments: [
            "Uncomplicated diverticulitis: often outpatient antibiotics or observation depending on severity",
            "Hospitalization and IV therapy for complicated cases",
            "Surgery in recurrent or complicated disease",
            "Endoscopic or interventional radiology therapy for bleeding when needed",
        ],
        lifestyle: [
            "High-fiber, whole-food dietary pattern",
            "Regular physical activity and healthy weight",
            "Individualized guidance — nuts and seeds are no longer restricted",
        ],
        when_to_seek_care: [
            "Abdominal pain with fever or vomiting",
            "Significant rectal bleeding",
            "Any change in bowel habits after age 45",
        ],
    },
];

export function getConditionBySlug(slug) {
    return CONDITIONS.find((c) => c.slug === slug);
}

export const CONDITION_CATEGORIES = [
    "All",
    "Upper GI",
    "Lower GI",
    "Inflammatory Bowel Disease",
    "Functional GI",
    "Liver",
    "Small Bowel",
    "General GI",
];
